import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Subscription, of, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { OrderTemplateService } from '@/app/shared/services/order-template.service';
import { ProductService } from '@/app/shared/services/product.service';
import { CartService } from '@/app/shared/services/cart.service';
import { OrderService } from '@/app/shared/services/order.service';
import { CommonService } from '@/app/shared/services/common.service';
import { ConfirmationModalComponent } from '@/app/shared/widgets/modal/confirmation-modal/confirmation-modal.component';
import {
  AddOrderTemplateToCartResponse,
  CreateOrderTemplateRequest,
  OrderTemplateItemResponse,
  OrderTemplateResponse,
  UpdateOrderTemplateRequest
} from '@/app/shared/interface/order-template.interface';
import { Order, OrderModel } from '@/app/shared/interface/order.interface';
import { normalizeOrderTemplateError } from '@/app/shared/utils/order-template-error';

@Component({
  selector: 'app-order-template',
  templateUrl: './order-template.component.html',
  styleUrls: ['./order-template.component.scss']
})
export class OrderTemplateComponent implements OnInit, OnDestroy {
  readonly unavailableProductImage = 'assets/images/order-template-product-unavailable.png';
  @ViewChild('deleteConfirmModal') deleteConfirmModal: ConfirmationModalComponent;
  @ViewChild('detailModal') detailModal: TemplateRef<any>;
  @ViewChild('createModal') createModal: TemplateRef<any>;
  @ViewChild('renameModal') renameModal: TemplateRef<any>;
  @ViewChild('deleteConfirmModalTpl') deleteConfirmModalTpl: TemplateRef<any>;

  private detailModalRef?: NgbModalRef;
  private createModalRef?: NgbModalRef;
  private renameModalRef?: NgbModalRef;
  private deleteModalRef?: NgbModalRef;

  // List data
  public templates: OrderTemplateResponse[] = [];
  public loading: boolean = false;
  public total: number = 0;
  public offset: number = 0;
  public limit: number = 20;
  public searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Detail & Modal state
  public selectedTemplate: OrderTemplateResponse | null = null;
  public isDetailOpen: boolean = false;
  public detailLoading: boolean = false;

  // Edit Mode state
  public isEditMode: boolean = false;
  public editTemplateName: string = '';
  public tempRename: string = '';
  public isEditingName: boolean = false;
  public editSearchTerm: string = '';
  public editItems: OrderTemplateItemResponse[] = [];
  public isSavingChanges: boolean = false;
  public detailReady: boolean = false;
  public addProductSearchTerm: string = '';
  public addProductResults: any[] = [];
  public isSearchingAddProducts: boolean = false;
  public addProductSearchError: boolean = false;
  private addProductSearchSubject = new Subject<string>();
  private addProductSearchSubscription?: Subscription;

  // Action states
  public isAddingToCart: { [id: string]: boolean } = {};
  public templateToDelete: OrderTemplateResponse | null = null;
  public isDeleting: boolean = false;

  // Create Modal state
  public isCreateModalOpen: boolean = false;
  public createTemplateName: string = '';
  public isSubmittingCreate: boolean = false;

  // Product Search state for Create Modal
  public productSearchTerm: string = '';
  public productSearchResults: any[] = [];
  public isSearchingProducts: boolean = false;
  public hasSearchedProducts: boolean = false;
  private productSearchSubject = new Subject<string>();
  private productSearchSubscription?: Subscription;

  // Selected items builder for new template
  public selectedCreateItems: Array<{
    productId?: number;
    sku: string;
    name: string;
    imageUrl?: string;
    price: number;
    packaging?: string;
    unit?: string;
    quantity: number;
  }> = [];

  // Quick import from orders
  public showOrderImportSelect: boolean = false;
  public selectedOrderId: string = '';
  public userOrders: Order[] = [];
  public loadingOrders: boolean = false;

  constructor(
    private orderTemplateService: OrderTemplateService,
    private productService: ProductService,
    private cartService: CartService,
    private orderService: OrderService,
    public commonService: CommonService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.offset = 0;
        this.loadTemplates();
      });

    this.productSearchSubscription = this.productSearchSubject
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((term) => {
        this.performProductSearch(term);
      });

    this.addProductSearchSubscription = this.addProductSearchSubject.pipe(
      switchMap((term) => {
        const searchTerm = term.trim();
        if (!searchTerm) return of([]);
        return timer(350).pipe(
          switchMap(() => this.productService.getProductsPagination(JSON.stringify({ limit: 15, searchTerm }))),
          map((res: any) => (res?.products || []).map((product: any) => ({ ...product, additionQuantity: 1 }))),
          catchError(() => {
            this.addProductSearchError = true;
            return of([]);
          })
        );
      })
    ).subscribe((products) => {
      this.addProductResults = products;
      this.isSearchingAddProducts = false;
    });

    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.addProductSearchSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.productSearchSubscription?.unsubscribe();
    this.detailModalRef?.close();
    this.createModalRef?.close();
    this.renameModalRef?.close();
    this.deleteModalRef?.close();
  }

  loadTemplates(): void {
    this.loading = true;
    this.orderTemplateService
      .getOrderTemplates(this.offset, this.limit, this.searchTerm)
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res && res.isSuccess && res.data) {
            this.templates = res.data.items || [];
            this.total = res.data.total || 0;
          } else {
            this.templates = [];
            this.total = 0;
            this.showTemplateError(res);
          }
        },
        error: (err) => {
          this.loading = false;
          this.templates = [];
          this.total = 0;
          console.error('Failed to load order templates', err);
          this.showTemplateError(err);
        }
      });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchTerm);
  }

  openDetail(template: OrderTemplateResponse): void {
    this.selectedTemplate = template;
    this.isDetailOpen = true;
    this.isEditMode = false;
    this.isEditingName = false;
    this.detailLoading = true;
    this.detailReady = false;

    if (isPlatformBrowser(this.platformId) && this.detailModal) {
      if (!this.detailModalRef) {
        this.detailModalRef = this.modalService.open(this.detailModal, {
          centered: true,
          backdrop: 'static',
          windowClass: 'order-template-detail-window',
          keyboard: false,
          backdropClass: 'order-template-modal-backdrop'
        });
        this.detailModalRef.result.then(
          () => {
            this.resetDetailState();
          },
          () => {
            this.resetDetailState();
          }
        );
      }
    }

    this.orderTemplateService.getOrderTemplateDetail(template.id).subscribe({
      next: (res) => {
        if (!this.isDetailOpen || this.selectedTemplate?.id !== template.id) return;
        this.detailLoading = false;
        if (res && res.isSuccess && res.data) {
          this.selectedTemplate = res.data;
          this.detailReady = Array.isArray(res.data.items);
        } else {
          this.showTemplateError(res, template.id);
        }
      },
      error: (err) => {
        if (!this.isDetailOpen || this.selectedTemplate?.id !== template.id) return;
        this.detailLoading = false;
        console.error('Failed to load template detail', err);
        this.showTemplateError(err, template.id);
      }
    });
  }

  closeDetail(): void {
    if (this.isSavingChanges) return;
    if (this.detailModalRef) {
      this.detailModalRef.close();
      this.detailModalRef = undefined;
    }
    this.resetDetailState();
  }

  private resetDetailState(): void {
    this.detailModalRef = undefined;
    this.isDetailOpen = false;
    this.isEditMode = false;
    this.isEditingName = false;
    this.selectedTemplate = null;
    this.editSearchTerm = '';
    this.detailReady = false;
    this.resetAddProductSearch();
  }

  // --- Edit Mode ---
  enterEditMode(): void {
    if (!this.selectedTemplate || !this.detailReady || this.detailLoading || this.isSavingChanges) return;
    this.isEditMode = true;
    this.isEditingName = false;
    this.editTemplateName = this.selectedTemplate.name || '';
    this.editSearchTerm = '';
    // Deep clone items
    this.editItems = (this.selectedTemplate.items || []).map((item) => ({ ...item }));
    this.resetAddProductSearch();
  }

  cancelEditMode(): void {
    if (this.isSavingChanges) return;
    this.isEditMode = false;
    this.isEditingName = false;
    this.editSearchTerm = '';
    this.resetAddProductSearch();
  }

  private resetAddProductSearch(): void {
    this.addProductSearchTerm = '';
    this.onAddProductSearchInput();
  }

  onAddProductSearchInput(): void {
    this.addProductResults = [];
    this.addProductSearchError = false;
    this.isSearchingAddProducts = !!this.addProductSearchTerm.trim();
    this.addProductSearchSubject.next(this.addProductSearchTerm);
  }

  addEditProduct(product: any): void {
    if (!this.isEditMode || this.isSavingChanges) return;
    const sku = String(product.sku || product.SKU || '').trim();
    const quantity = Number(product.additionQuantity);
    if (!sku) {
      this.toastr.warning('Sản phẩm không có mã SKU hợp lệ.');
      return;
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      this.toastr.warning('Vui lòng nhập số lượng thêm là số nguyên lớn hơn 0.');
      return;
    }
    const existing = this.editItems.find((item) => item.sku.trim().toLowerCase() === sku.toLowerCase());
    if (existing) {
      existing.quantity += quantity;
      existing.lineSubtotal = (existing.displayPrice || 0) * existing.quantity;
    } else {
      const price = product.pricingTablePrice || product.basePrice || 0;
      this.editItems.push({
        sku, quantity, availability: 'Available',
        productId: product.productID,
        productName: product.name || sku,
        imageUrl: product.imageUrls?.[0] || product.imageUrl,
        displayPrice: price,
        basePrice: product.basePrice,
        lineSubtotal: price * quantity,
        stockStatus: product.stockStatus
      });
    }
    this.editSearchTerm = '';
    this.toastr.success(`Đã thêm ${quantity} sản phẩm vào danh sách. Bấm Lưu thay đổi để lưu.`);
  }

  openRenameModal(): void {
    if (!this.selectedTemplate) return;
    this.tempRename = this.editTemplateName;
    if (isPlatformBrowser(this.platformId) && this.renameModal) {
      this.renameModalRef = this.modalService.open(this.renameModal, {
        centered: true,
        windowClass: 'order-template-mini-window',
        backdropClass: 'order-template-mini-backdrop'
      });
      this.renameModalRef.result.then(
        () => (this.renameModalRef = undefined),
        () => (this.renameModalRef = undefined)
      );
    }
  }

  saveRename(modal?: any): void {
    if (this.tempRename && this.tempRename.trim()) {
      this.editTemplateName = this.tempRename.trim();
    }
    if (modal) {
      modal.close();
    } else if (this.renameModalRef) {
      this.renameModalRef.close();
      this.renameModalRef = undefined;
    }
  }

  cancelRename(modal?: any): void {
    if (modal) {
      modal.dismiss();
    } else if (this.renameModalRef) {
      this.renameModalRef.dismiss();
      this.renameModalRef = undefined;
    }
  }

  get filteredEditItems(): OrderTemplateItemResponse[] {
    if (!this.editSearchTerm || !this.editSearchTerm.trim()) {
      return this.editItems;
    }
    const term = this.editSearchTerm.trim().toLowerCase();
    return this.editItems.filter(
      (i) =>
        (i.productName && i.productName.toLowerCase().includes(term)) ||
        (i.sku && i.sku.toLowerCase().includes(term))
    );
  }

  changeQty(item: OrderTemplateItemResponse, delta: number): void {
    if (this.isSavingChanges) return;
    const nextQty = (item.quantity || 1) + delta;
    if (nextQty >= 1) {
      item.quantity = nextQty;
      if (item.displayPrice) {
        item.lineSubtotal = item.displayPrice * item.quantity;
      }
    }
  }

  validateQty(item: OrderTemplateItemResponse): void {
    if (this.isSavingChanges) return;
    let q = Math.floor(Number(item.quantity));
    if (isNaN(q) || q < 1) q = 1;
    item.quantity = q;
    if (item.displayPrice) {
      item.lineSubtotal = item.displayPrice * item.quantity;
    }
  }

  removeItem(item: OrderTemplateItemResponse): void {
    if (this.isSavingChanges) return;
    if (this.editItems.length <= 1) {
      this.toastr.warning('Đơn hàng mẫu phải có ít nhất 1 sản phẩm.');
      return;
    }
    this.editItems = this.editItems.filter((i) => i.sku !== item.sku);
  }

  getEditEstimatedSubtotal(): number {
    return this.editItems
      .filter((i) => i.stockStatus !== 'OutOfStock' && i.displayPrice)
      .reduce((acc, curr) => acc + (curr.lineSubtotal || (curr.displayPrice || 0) * curr.quantity), 0);
  }

  saveChanges(): void {
    if (!this.selectedTemplate || this.isSavingChanges || !this.detailReady) return;
    if (this.editItems.length === 0) {
      this.toastr.warning('Vui lòng giữ lại ít nhất 1 sản phẩm.');
      return;
    }

    if (this.editItems.some((item) => !item.sku.trim() || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) {
      this.toastr.warning('Mã SKU và số lượng sản phẩm không hợp lệ.');
      return;
    }
    const payload: UpdateOrderTemplateRequest = {
      name: this.editTemplateName ? this.editTemplateName.trim() : this.selectedTemplate.name,
      items: this.editItems.map((i) => ({
        sku: i.sku,
        quantity: i.quantity
      }))
    };

    this.isSavingChanges = true;
    this.orderTemplateService.updateOrderTemplate(this.selectedTemplate.id, payload).subscribe({
      next: (res) => {
        this.isSavingChanges = false;
        if (res && res.isSuccess) {
          this.toastr.success('Cập nhật đơn hàng thường xuyên thành công!');
          this.isEditMode = false;
          this.isEditingName = false;
          this.resetAddProductSearch();
          if (this.selectedTemplate) {
            if (payload.name) {
              this.selectedTemplate.name = payload.name;
            }
            // Reload detail to fetch fresh prices and availability
            const currentId = this.selectedTemplate.id;
            this.detailLoading = true;
            this.detailReady = false;
            this.orderTemplateService.getOrderTemplateDetail(currentId).subscribe({
              next: (detailRes) => {
                if (!this.isDetailOpen || this.selectedTemplate?.id !== currentId) return;
                this.detailLoading = false;
                if (detailRes && detailRes.isSuccess && detailRes.data) {
                  this.selectedTemplate = detailRes.data;
                  this.detailReady = Array.isArray(detailRes.data.items);
                } else {
                  this.showTemplateError(detailRes, currentId, 'Đã lưu thay đổi. ');
                }
              },
              error: (err) => {
                if (!this.isDetailOpen || this.selectedTemplate?.id !== currentId) return;
                this.detailLoading = false;
                this.showTemplateError(err, currentId, 'Đã lưu thay đổi. ');
              }
            });
          }
          this.loadTemplates();
        } else {
          this.showTemplateError(res, this.selectedTemplate?.id);
        }
      },
      error: (err) => {
        this.isSavingChanges = false;
        console.error('Update template error', err);
        this.showTemplateError(err, this.selectedTemplate?.id);
      }
    });
  }

  // --- Add To Cart ---
  onAddToCart(template: OrderTemplateResponse, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isAddingToCart[template.id]) return;

    this.isAddingToCart[template.id] = true;
    this.orderTemplateService.addToCart(template.id, {}).subscribe({
      next: (res) => {
        this.isAddingToCart[template.id] = false;
        if (res && res.isSuccess && res.data) {
          const data: AddOrderTemplateToCartResponse = res.data;
          if (data.cart) {
            this.commonService.setCart(data.cart);
          } else {
            this.cartService.refreshCart();
          }

          const totalAdded = (data.addedItemCount || 0) + (data.partiallyAddedItemCount || 0);
          if (totalAdded > 0) {
            this.toastr.success(`Đã thêm ${totalAdded} sản phẩm vào giỏ hàng!`);
          } else {
            this.toastr.error(res.message || 'Không có sản phẩm nào được thêm vào giỏ hàng.');
          }

          // Reload list to update lastUsedText
          this.loadTemplates();
          if (this.selectedTemplate && this.selectedTemplate.id === template.id) {
            this.orderTemplateService.getOrderTemplateDetail(template.id).subscribe({
              next: (dRes) => {
                if (dRes && dRes.isSuccess && dRes.data) {
                  this.selectedTemplate = dRes.data;
                } else {
                  this.showTemplateError(dRes, template.id);
                }
              },
              error: (err) => {
                this.showTemplateError(err, template.id);
              }
            });
          }
        } else {
          this.showTemplateError(res, template.id);
        }
      },
      error: (err) => {
        this.isAddingToCart[template.id] = false;
        console.error('Add to cart error', err);
        this.showTemplateError(err, template.id);
      }
    });
  }

  // --- Delete Template ---
  onDeleteClick(template: OrderTemplateResponse, event?: Event): void {
    if (event) event.stopPropagation();
    this.templateToDelete = template;
    if (isPlatformBrowser(this.platformId) && this.deleteConfirmModalTpl) {
      this.deleteModalRef = this.modalService.open(this.deleteConfirmModalTpl, {
        centered: true,
        windowClass: 'order-template-mini-window',
        backdropClass: 'order-template-mini-backdrop'
      });
      this.deleteModalRef.result.then(
        () => (this.deleteModalRef = undefined),
        () => {
          this.deleteModalRef = undefined;
          this.templateToDelete = null;
        }
      );
    }
  }

  confirmDelete(modal?: any): void {
    if (!this.templateToDelete || this.isDeleting) return;

    const templateId = this.templateToDelete.id;
    this.isDeleting = true;
    this.orderTemplateService.deleteOrderTemplate(templateId).subscribe({
      next: () => {
        this.isDeleting = false;
        this.toastr.success('Đã xóa đơn hàng mẫu thành công!');
        if (modal) {
          modal.close();
        } else if (this.deleteModalRef) {
          this.deleteModalRef.close();
          this.deleteModalRef = undefined;
        }
        if (this.selectedTemplate && this.selectedTemplate.id === templateId) {
          this.closeDetail();
        }
        this.templateToDelete = null;
        this.loadTemplates();
      },
      error: (err) => {
        this.isDeleting = false;
        console.error('Delete template error', err);
        this.showTemplateError(err, templateId);
      }
    });
  }

  cancelDelete(modal?: any): void {
    if (modal) {
      modal.dismiss();
    } else if (this.deleteModalRef) {
      this.deleteModalRef.dismiss();
      this.deleteModalRef = undefined;
    }
    this.templateToDelete = null;
  }

  // --- Create Modal ---
  openCreateModal(): void {
    this.createTemplateName = '';
    this.productSearchTerm = '';
    this.productSearchResults = [];
    this.isSearchingProducts = false;
    this.hasSearchedProducts = false;
    this.selectedCreateItems = [];
    this.showOrderImportSelect = false;
    this.selectedOrderId = '';
    this.userOrders = [];
    this.loadingOrders = false;
    this.isSubmittingCreate = false;

    if (isPlatformBrowser(this.platformId) && this.createModal) {
      this.createModalRef = this.modalService.open(this.createModal, {
        centered: true,
        backdrop: 'static',
        windowClass: 'order-template-create-window',
        backdropClass: 'order-template-modal-backdrop'
      });
      this.createModalRef.result.then(
        () => (this.createModalRef = undefined),
        () => (this.createModalRef = undefined)
      );
    }
  }

  closeCreateModal(): void {
    if (this.createModalRef) {
      this.createModalRef.close();
      this.createModalRef = undefined;
    }
  }

  onProductSearchInput(): void {
    if (!this.productSearchTerm || !this.productSearchTerm.trim()) {
      this.productSearchResults = [];
      this.hasSearchedProducts = false;
      this.isSearchingProducts = false;
      return;
    }
    this.productSearchSubject.next(this.productSearchTerm);
  }

  performProductSearch(term: string): void {
    const trimmed = (term || '').trim();
    if (!trimmed) {
      this.productSearchResults = [];
      this.hasSearchedProducts = false;
      this.isSearchingProducts = false;
      return;
    }

    this.isSearchingProducts = true;
    this.hasSearchedProducts = true;

    const body = {
      limit: 15,
      searchTerm: trimmed
    };

    this.productService.getProductsPagination(JSON.stringify(body)).subscribe({
      next: (res: any) => {
        this.isSearchingProducts = false;
        if (res && res.products) {
          this.productSearchResults = res.products;
        } else {
          this.productSearchResults = [];
        }
      },
      error: (err) => {
        this.isSearchingProducts = false;
        this.productSearchResults = [];
        console.error('Failed to search products', err);
      }
    });
  }

  clearProductSearch(): void {
    this.productSearchTerm = '';
    this.productSearchResults = [];
    this.hasSearchedProducts = false;
    this.isSearchingProducts = false;
  }

  addCreateProduct(p: any): void {
    const sku = p.sku || p.SKU || (p.productID ? p.productID.toString() : '');
    if (!sku) {
      this.toastr.warning('Không tìm thấy SKU của sản phẩm.');
      return;
    }

    const existing = this.selectedCreateItems.find((item) => item.sku === sku);
    if (existing) {
      existing.quantity += 1;
      this.toastr.info(`Đã tăng số lượng "${existing.name}" lên ${existing.quantity}`);
      return;
    }

    const price = p.pricingTablePrice || p.basePrice || 0;
    const imageUrl =
      p.imageUrls && p.imageUrls.length > 0
        ? p.imageUrls[0]
        : p.imageUrl || 'assets/images/placeholder.png';
    const packaging = p.packagingSpecifications || p.unit || '';

    this.selectedCreateItems.push({
      productId: p.productID,
      sku: sku,
      name: p.name || 'Sản phẩm',
      imageUrl: imageUrl,
      price: price,
      packaging: packaging,
      unit: p.unit || '',
      quantity: 1
    });

    this.toastr.success(`Đã thêm "${p.name || sku}" vào danh sách.`);
  }

  isProductAdded(sku: string): boolean {
    if (!sku) return false;
    return this.selectedCreateItems.some((i) => i.sku === sku);
  }

  getProductAddedQty(sku: string): number {
    if (!sku) return 0;
    const found = this.selectedCreateItems.find((i) => i.sku === sku);
    return found ? found.quantity : 0;
  }

  changeCreateQty(item: any, delta: number): void {
    const nextQty = (item.quantity || 1) + delta;
    if (nextQty >= 1) {
      item.quantity = nextQty;
    }
  }

  validateCreateQty(item: any): void {
    let q = Math.floor(Number(item.quantity));
    if (isNaN(q) || q < 1) q = 1;
    item.quantity = q;
  }

  removeCreateItem(item: any): void {
    this.selectedCreateItems = this.selectedCreateItems.filter((i) => i.sku !== item.sku);
  }

  clearAllCreateItems(): void {
    this.selectedCreateItems = [];
  }

  getCreateItemsTotalQuantity(): number {
    return this.selectedCreateItems.reduce((acc, curr) => acc + (curr.quantity || 1), 0);
  }

  getCreateItemsEstimatedTotal(): number {
    return this.selectedCreateItems.reduce(
      (acc, curr) => acc + (curr.price || 0) * (curr.quantity || 1),
      0
    );
  }

  importFromCart(): void {
    this.cartService.refreshCart();
    const sub = this.commonService.getCart.subscribe({
      next: (cart: any) => {
        sub.unsubscribe();
        if (!cart || !cart.items || cart.items.length === 0) {
          this.toastr.warning('Giỏ hàng hiện tại đang trống.');
          return;
        }

        let addedCount = 0;
        cart.items.forEach((ci: any) => {
          const sku = ci.SKU || ci.sku;
          if (!sku) return;

          const existing = this.selectedCreateItems.find((i) => i.sku === sku);
          if (existing) {
            existing.quantity = existing.quantity + (ci.quantity || 1);
          } else {
            this.selectedCreateItems.push({
              productId: ci.productID,
              sku: sku,
              name: ci.name,
              imageUrl: ci.imgUrl || 'assets/images/placeholder.png',
              price: ci.price || ci.basePrice || 0,
              packaging: ci.packagingSpecifications || '',
              unit: '',
              quantity: ci.quantity || 1
            });
            addedCount++;
          }
        });

        this.toastr.success(`Đã lấy ${cart.items.length} sản phẩm từ giỏ hàng!`);
      },
      error: (err) => {
        sub.unsubscribe();
        console.error(err);
        this.toastr.error('Không thể lấy sản phẩm từ giỏ hàng.');
      }
    });
  }

  toggleOrderImport(): void {
    this.showOrderImportSelect = !this.showOrderImportSelect;
    if (this.showOrderImportSelect) {
      this.selectedOrderId = '';
      this.userOrders = [];
      this.loadUserOrders();
    }
  }

  loadUserOrders(): void {
    this.loadingOrders = true;
    const body = { limit: 10, offset: 0 };
    this.orderService.getOrders(JSON.stringify(body)).subscribe({
      next: (res: OrderModel) => {
        this.loadingOrders = false;
        if (res && res.orderHistory) {
          this.userOrders = res.orderHistory;
          // Không tự động chọn đơn – người dùng phải chọn thủ công
        }
      },
      error: (err) => {
        this.loadingOrders = false;
        console.error('Failed to load user orders', err);
      }
    });
  }

  importFromSelectedOrder(): void {
    if (!this.selectedOrderId) {
      this.toastr.warning('Vui lòng chọn một đơn hàng.');
      return;
    }

    const order = this.userOrders.find((o) => o.orderId === this.selectedOrderId);
    if (!order || !order.items || order.items.length === 0) {
      this.toastr.warning('Đơn hàng đã chọn không có sản phẩm.');
      return;
    }

    let addedCount = 0;
    order.items.forEach((oi: any) => {
      const sku = oi.sku || oi.SKU || oi.skU2;
      if (!sku) return;

      const existing = this.selectedCreateItems.find((i) => i.sku === sku);
      if (existing) {
        existing.quantity = existing.quantity + (oi.quantity || 1);
      } else {
        this.selectedCreateItems.push({
          productId: oi.productID,
          sku: sku,
          name: oi.name,
          imageUrl: oi.imgUrl || 'assets/images/placeholder.png',
          price: oi.price || 0,
          packaging: '',
          unit: '',
          quantity: oi.quantity || 1
        });
        addedCount++;
      }
    });

    this.toastr.success(`Đã lấy ${order.items.length} sản phẩm từ đơn hàng ${order.orderId}!`);
    this.showOrderImportSelect = false;
  }

  submitCreate(): void {
    if (this.isSubmittingCreate) return;

    if (this.selectedCreateItems.length === 0) {
      this.toastr.warning('Vui lòng tìm kiếm và thêm ít nhất 1 sản phẩm vào đơn hàng thường xuyên.');
      return;
    }

    this.isSubmittingCreate = true;
    const name = this.createTemplateName ? this.createTemplateName.trim() : undefined;
    const items = this.selectedCreateItems.map((item) => ({
      sku: item.sku,
      quantity: item.quantity
    }));

    this.orderTemplateService.createOrderTemplate({ name, items }).subscribe({
      next: (res) => {
        this.isSubmittingCreate = false;
        if (res && res.isSuccess && res.data) {
          this.toastr.success('Đã tạo đơn hàng thường xuyên thành công!');
          this.closeCreateModal();
          this.loadTemplates();
          this.openDetail(res.data);
        } else {
          this.showTemplateError(res);
        }
      },
      error: (err) => {
        this.isSubmittingCreate = false;
        console.error('Create order template error', err);
        this.showTemplateError(err);
      }
    });
  }

  // Helper methods
  private showTemplateError(error: unknown, templateId?: string, prefix: string = ''): void {
    const normalized = normalizeOrderTemplateError(error);
    this.toastr.error(prefix + normalized.message);
    console.error('Order template API error', normalized);
    if (normalized.code === 'TEMPLATE_NOT_FOUND' && templateId) {
      if (this.selectedTemplate?.id === templateId) this.closeDetail();
      if (this.templateToDelete?.id === templateId) {
        this.cancelDelete();
      }
      this.loadTemplates();
    }
  }

  getDiscountPercent(basePrice?: number | null, displayPrice?: number | null): number {
    if (!basePrice || !displayPrice || basePrice <= displayPrice) return 0;
    return Math.round(((basePrice - displayPrice) / basePrice) * 100);
  }

  getAvailabilityText(status: string): string {
    switch (status) {
      case 'NotFound':
        return 'Sản phẩm không tồn tại';
      case 'OutOfScope':
        return 'Không thuộc phạm vi kinh doanh';
      case 'Discontinued':
        return 'Sản phẩm đã ngừng kinh doanh';
      case 'Unavailable':
        return 'Không thể mua / hết hàng';
      case 'SkuChanged':
        return 'Mã sản phẩm đã thay đổi';
      default:
        return status;
    }
  }
}
